import { t, Trans } from "@lingui/macro";
import React, { useEffect, useState } from "react";
import { ViewDataProductInfo } from "./ViewComponent";
import { useDispatch, useSelector } from "react-redux";
import { edmIsEnabled } from "../../../../utils/edmIsEnabled";
import { useInputChange } from "../../../../hooks/useInputChange";
import { ISystemState } from "../../../../model/system/systemReducer";
import { typeRequestPagination } from "../../../../model/system/constants";
import { typePaginationDashboardObject } from "../DashboardContentTable/PaginationDashboard/PaginationDashboard";

import {
  USERPOSITIONS,
  POSITION_ALLOW_FULL_CRUD_PRODUCTS,
} from "../../../../constants/userPositions";
import {
  IdProductsState,
  typeProductsFilters,
} from "../../../../model/dashboardProducts/dProductsReducer";
import {
  dProductsSetFiltersAC,
  dProductsSetDataSagaAC,
  dProductsClearFiltersAC,
  dProductsDeleteAllSagaAC,
  dProductsSetDetailItemAC,
  dProductsSetPageNumberAC,
  dProductsDeleteItemSagaAC,
  dProductsBulkDeleteSagaAC,
  dProductsSetItemsForPageAC,
  dProductsRecoverItemSagaAC,
  dProductsBulkRecoverySagaAC,
} from "../../../../model/dashboardProducts/dProductsActionCreators";
import { checkExistPSID } from "../../../../model/dashboardProducts/dProductsAPI";
import { addNotificationAC } from "../../../../model/system/systemActionCreators";

export const psidValidation = (psid: string) => {
  const str = psid.trim();
  return str.length === 17 && /^\d{17}$/.test(str);
};

export type typeDataProductInfo = {
  variant?: string;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export type typeDataProductInfoRowActions = {
  id?: string;
  key?: string;
  title: JSX.Element;
  action: (id: string) => void;
}[];

export type typeDataProductInfoBulkActions = {
  action: () => void;
  title: React.JSX.Element;
}[];

export const DataProductInfo: React.FC<typeDataProductInfo> = ({
  variant,
  setSidebarOpen,
}) => {
  const dispatch = useDispatch();
  const position = useSelector(
    (state: { system: ISystemState }) => state.system.userInfo?.position
  );

  const [openFilterBar, setOpenFilterBar] = useState(false);
  const [isActiveFilters, setIsActiveFilters] = useState(false);
  const filters = useSelector(
    (state: { dProducts: IdProductsState }) => state.dProducts.filters
  );

  const pagination: typePaginationDashboardObject = {
    paginationObject: useSelector(
      (state: { dProducts: IdProductsState }) => state.dProducts.pagination
    ),
    onGoToPage: (id) => dispatch(dProductsSetPageNumberAC(id)),
    onSelectItems: (itemsPerPage) =>
      dispatch(dProductsSetItemsForPageAC(itemsPerPage)),
  };

  const makePaginationRequest = (
    pagination: typePaginationDashboardObject
  ): typeRequestPagination => {
    return {
      page: pagination.paginationObject.pageNumber,
      size: pagination.paginationObject.itemsPerPage!,
    };
  };

  const dataList = useSelector(
    (state: { dProducts: IdProductsState }) => state.dProducts.data
  );

  useEffect(() => {
    return () => {
      dispatch(dProductsClearFiltersAC());
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(
      dProductsSetDataSagaAC(filters, {
        page: pagination.paginationObject.pageNumber,
        size: pagination.paginationObject.itemsPerPage!,
      })
    );
    // eslint-disable-next-line
  }, [
    filters,
    pagination.paginationObject.pageNumber,
    pagination.paginationObject.itemsPerPage,
  ]);

  const searchName = useSelector(
    (state: { dProducts: IdProductsState }) => state.dProducts.filters.name
  );
  const {
    value: searchValue,
    setValue: setSearchValue,
    changeValueHandler: onChangeSearchValue,
  } = useInputChange(searchName || "");

  const onClearSearch = () => {
    dispatch(dProductsSetFiltersAC({ name: null }));
    setSearchValue("");
  };
  const onEnterSearch = () => {
    const filters: typeProductsFilters = {
      name: searchValue.trim(),
    };
    dispatch(dProductsSetFiltersAC(filters));
  };

  const [openPopupAdd, setOpenPopupAdd] = useState(false);
  const [openPopupEdm, setOpenPopupEdm] = useState(false);
  const [openPopupEdit, setOpenPopupEdit] = useState(false);
  const [openPopupImport, setOpenPopupImport] = useState(false);
  const [openPopupExport, setOpenPopupExport] = useState(false);
  const [openPopupDetails, setOpenPopupDetails] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const onDeleteConfirmHandler = () => {
    dispatch(
      dProductsDeleteItemSagaAC(
        currentItem!.id,
        filters,
        makePaginationRequest(pagination)
      )
    );
    setOpenDeleteDialog(false);
  };

  const onDeleteCancelHandler = () => {
    setOpenDeleteDialog(false);
  };

  const [openRecoverDialog, setOpenRecoverDialog] = useState(false);

  const onRecoverConfirmHandler = () => {
    dispatch(
      dProductsRecoverItemSagaAC(
        currentItem!.id,
        filters,
        makePaginationRequest(pagination)
      )
    );
    setOpenRecoverDialog(false);
  };

  const onRecoverCancelHandler = () => {
    setOpenRecoverDialog(false);
  };

  const [openPopupBulkChangeCategory, setOpenPopupBulkChangeCategory] =
    useState(false);

  const [openPopupBulkOffSale, setOpenPopupBulkOffSale] = useState(false);

  const [openPopupBulkDeleteFromStores, setOpenPopupBulkDeleteFromStores] =
    useState(false);

  const [openBulkDeleteDialog, setBulkOpenDeleteDialog] = useState(false);

  const onBulkDeleteConfirmHandler = () => {
    const bulkIdList: string[] = dataList
      .filter((item) => item.checked)
      .map((item) => item.id);

    dispatch(
      dProductsBulkDeleteSagaAC(bulkIdList, filters, {
        page: pagination.paginationObject.pageNumber,
        size: pagination.paginationObject.itemsPerPage!,
      })
    );
    setBulkOpenDeleteDialog(false);
  };
  const onBulkDeleteCancelHandler = () => {
    setBulkOpenDeleteDialog(false);
  };

  const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);

  const [openChangeVATDialog, setOpenChangeVATDialog] = useState(false);

  const onDeleteAllConfirmHandler = () => {
    dispatch(
      dProductsDeleteAllSagaAC(
        { deleted: false },
        {
          page: pagination.paginationObject.pageNumber,
          size: pagination.paginationObject.itemsPerPage!,
        }
      )
    );
    setOpenDeleteAllDialog(false);
  };

  const onDeleteAllCancelHandler = () => {
    setOpenDeleteAllDialog(false);
  };

  const [openBulkRecoveryDialog, setOpenBulkRecoveryDialog] = useState(false);

  const onBulkRecoveryConfirmHandler = () => {
    const bulkIdList: string[] = dataList
      .filter((item) => item.checked && item.deleted)
      .map((item) => item.id);

    dispatch(
      dProductsBulkRecoverySagaAC(bulkIdList, filters, {
        page: pagination.paginationObject.pageNumber,
        size: pagination.paginationObject.itemsPerPage!,
      })
    );
    setOpenBulkRecoveryDialog(false);
  };

  const onBulkRecoveryCancelHandler = () => {
    setOpenBulkRecoveryDialog(false);
  };

  const currentItem = useSelector(
    (state: { dProducts: IdProductsState }) => state.dProducts.detailItem
  );

  const onAddToEdm = async (id: string, psid: string) => {
    const notificationId = new Date();

    try {
      if (psid) {
        const res = await checkExistPSID({ psid });

        if (res.status === 200) {
          dispatch(dProductsSetDetailItemAC(id));
          setOpenPopupEdm(true);
        } else {
          dispatch(
            addNotificationAC({
              id: notificationId.getTime().toString(),
              message: t`Incorrect PSID. Please make sure that the PSID is correct and try again.`,
            })
          );
        }
      } else {
        dispatch(
          addNotificationAC({
            id: notificationId.getTime().toString(),
            message: t`Incorrect PSID. Please make sure that the PSID is correct and try again.`,
          })
        );
      }
    } catch (e) {
      dispatch(
        addNotificationAC({
          id: notificationId.getTime().toString(),
          message: t`Incorrect PSID. Please make sure that the PSID is correct and try again.`,
        })
      );
    }
  };

  const rowsActions: typeDataProductInfoRowActions = [
    {
      title: <Trans>Edit</Trans>,
      action: (id: string) => {
        dispatch(dProductsSetDetailItemAC(id));
        setOpenPopupEdit(true);
      },
    },
    {
      key: "add_to_edm",
      title: <Trans>Add to Warehouse</Trans>,
      action: (id: string) => {
        const product = dataList.find((item) => item.id === id);

        onAddToEdm(id, product?.psid || "").catch(console.error);
      },
    },
    {
      id: "recover",
      title: <Trans>Recover</Trans>,
      action: (id: string) => {
        dispatch(dProductsSetDetailItemAC(id));
        setOpenRecoverDialog(true);
      },
    },
    {
      id: "delete",
      title: <Trans>Delete</Trans>,
      action: (id: string) => {
        dispatch(dProductsSetDetailItemAC(id));
        setOpenDeleteDialog(true);
      },
    },
  ].filter((item) => {
    if (edmIsEnabled) {
      return true;
    } else {
      return item.key !== "add_to_edm";
    }
  });

  const bulkActions: typeDataProductInfoBulkActions =
    POSITION_ALLOW_FULL_CRUD_PRODUCTS.includes(position as USERPOSITIONS)
      ? [
          {
            title: <Trans>Selected export</Trans>,
            action: () => setOpenPopupExport(true),
          },
          {
            title: <Trans>Change category</Trans>,
            action: () => setOpenPopupBulkChangeCategory(true),
          },
          {
            title: <Trans>Delete from stores</Trans>,
            action: () => setOpenPopupBulkDeleteFromStores(true),
          },
          {
            title: <Trans>Delete</Trans>,
            action: () => setBulkOpenDeleteDialog(true),
          },
          {
            title: <Trans>Recover</Trans>,
            action: () => setOpenBulkRecoveryDialog(true),
          },
        ]
      : [
          {
            title: <Trans>Selected export</Trans>,
            action: () => setOpenPopupExport(true),
          },
        ];

  const onClickAdd = () => {
    setOpenPopupAdd(true);
  };

  const onClickImport = () => {
    setOpenPopupImport(true);
  };

  const onClickExport = () => {
    setOpenPopupExport(true);
  };

  const onClickChangeVAT = () => {
    setOpenChangeVATDialog(true);
  };

  return (
    <ViewDataProductInfo
      variant={variant}
      setSidebarOpen={setSidebarOpen}
      openPopupEdm={openPopupEdm}
      setOpenPopupEdm={setOpenPopupEdm}
      onClickAdd={onClickAdd}
      onClickImport={onClickImport}
      onClickExport={onClickExport}
      onClickChangeVAT={onClickChangeVAT}
      openFilterBar={openFilterBar}
      setOpenFilterBar={setOpenFilterBar}
      openPopupImport={openPopupImport}
      setOpenPopupImport={setOpenPopupImport}
      openPopupExport={openPopupExport}
      setOpenPopupExport={setOpenPopupExport}
      openPopupDetails={openPopupDetails}
      setOpenPopupDetails={setOpenPopupDetails}
      openPopupAdd={openPopupAdd}
      setOpenPopupAdd={setOpenPopupAdd}
      openPopupEdit={openPopupEdit}
      setOpenPopupEdit={setOpenPopupEdit}
      openDeleteDialog={openDeleteDialog}
      setOpenDeleteDialog={setOpenDeleteDialog}
      onDeleteConfirmHandler={onDeleteConfirmHandler}
      onDeleteCancelHandler={onDeleteCancelHandler}
      openRecoverDialog={openRecoverDialog}
      setOpenRecoverDialog={setOpenRecoverDialog}
      onRecoverConfirmHandler={onRecoverConfirmHandler}
      onRecoverCancelHandler={onRecoverCancelHandler}
      openPopupBulkChangeCategory={openPopupBulkChangeCategory}
      setOpenPopupBulkChangeCategory={setOpenPopupBulkChangeCategory}
      openPopupBulkOffSale={openPopupBulkOffSale}
      setOpenPopupBulkOffSale={setOpenPopupBulkOffSale}
      openPopupBulkDeleteFromStores={openPopupBulkDeleteFromStores}
      setOpenPopupBulkDeleteFromStores={setOpenPopupBulkDeleteFromStores}
      openBulkDeleteDialog={openBulkDeleteDialog}
      setBulkOpenDeleteDialog={setBulkOpenDeleteDialog}
      onBulkDeleteConfirmHandler={onBulkDeleteConfirmHandler}
      onBulkDeleteCancelHandler={onBulkDeleteCancelHandler}
      deleteCount={dataList.filter((item) => item.checked).length}
      openDeleteAllDialog={openDeleteAllDialog}
      setOpenDeleteAllDialog={setOpenDeleteAllDialog}
      onDeleteAllCancelHandler={onDeleteAllCancelHandler}
      onDeleteAllConfirmHandler={onDeleteAllConfirmHandler}
      openChangeVATDialog={openChangeVATDialog}
      setOpenChangeVATDialog={setOpenChangeVATDialog}
      openBulkRecoveryDialog={openBulkRecoveryDialog}
      setOpenBulkRecoveryDialog={setOpenBulkRecoveryDialog}
      onBulkRecoveryCancelHandler={onBulkRecoveryCancelHandler}
      onBulkRecoveryConfirmHandler={onBulkRecoveryConfirmHandler}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      onChangeSearchValue={onChangeSearchValue}
      onEnterSearch={onEnterSearch}
      onClearSearch={onClearSearch}
      rowsActions={rowsActions}
      bulkActions={bulkActions}
      currentItem={currentItem}
      pagination={pagination}
      isActiveFilters={isActiveFilters}
      setIsActiveFilters={setIsActiveFilters}
      position={position!}
    />
  );
};
